import React, { useEffect, useRef, useState } from "react";
import Button from "components/atoms/Button";
import InputSelect from "components/atoms/InputSelect";
import InputDate from "components/atoms/InputDate";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { confirmAlert } from "react-confirm-alert";
import InputCheckbox from "components/atoms/InputCheckbox";
import { SampleSendTypeOptions } from "utils/constant";
import { initialSendDate, sampleSendDateErrors, sampleTypeError } from "./sampleSendRules";

const ModalSendLab = ({
  isOpen,
  onClose,
  onSubmit,
  labOptions = [],
  initialData = null,
  modalRow,
  isSaving = false,
  requireConfirmation = false,
}) => {
  // Default destination_lab_id to Lab Tier 4 if not already set
  const getDefaultLabId = () => {
    if (modalRow?.destination_lab_id) return modalRow.destination_lab_id;
    const tier4Lab = labOptions.find((opt) => opt.level === "LabTier4");
    return tier4Lab ? tier4Lab.value : "";
  };

  const initialValues = {
    smf_id: modalRow?.smf_id,
    skl34_id: modalRow?.skl34_id,
    skl5_id: modalRow?.skl5_id,
    regmal_id: modalRow?.id,
    sample_send_type: Array.isArray(modalRow?.sample_send_type)
      ? modalRow.sample_send_type
      : [],
    destination_lab_id: getDefaultLabId(),
    sample_send_date: initialSendDate(modalRow || {}) || "",
  };

  // CSS bawaan library memakai z-index 99, sedangkan modal pengiriman 1050.
  // Tanpa ini dialog tertutup modal: tidak terlihat dan tidak bisa diklik.
  const raiseAlertAboveModal = () => {
    const overlay = document.querySelector(".react-confirm-alert-overlay");
    if (overlay) overlay.style.zIndex = 2000;
  };

  const [showSendingInfo, setShowSendingInfo] = useState(false);
  const submitLockRef = useRef(false);

  const askSendConfirmation = () =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (confirmed) => {
        if (settled) return;
        settled = true;
        resolve(confirmed);
      };

      confirmAlert({
        title: "Apakah yakin ingin mengirimkan Sediaan Darah?",
        message: "Data yang sudah dikirim tidak dapat dihapus.",
        buttons: [
          {
            label: "Ya, kirim",
            onClick: () => finish(true),
          },
          {
            label: "Batal",
            onClick: () => finish(false),
          },
        ],
        // Escape, klik di luar, atau tombol Batal sama-sama membatalkan.
        willUnmount: () => finish(false),
      });

      raiseAlertAboveModal();
    });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues,
    validationSchema: () =>
      Yup.object({
        destination_lab_id: Yup.string().required("Lab tujuan wajib dipilih"),
        sample_send_type: Yup.array().test(
          "sample-type",
          "",
          function () {
            const error = sampleTypeError(this.parent.sample_send_type);
            return error ? this.createError({ message: error }) : true;
          }
        ),
        sample_send_date: Yup.string().test(
          "send-date",
          "",
          function () {
            const error = sampleSendDateErrors(this.parent).sample_send_date;
            return error ? this.createError({ message: error }) : true;
          }
        ),
      }),
    onSubmit: async (values, helpers) => {
      if (submitLockRef.current) return;
      submitLockRef.current = true;
      try {
        // Validasi Formik sudah lewat sebelum blok ini; Kabko minta konfirmasi dulu.
        if (requireConfirmation) {
          const confirmed = await askSendConfirmation();
          if (!confirmed) return;
        }
        const saved = await onSubmit?.(values);
        // Hanya sukses eksplisit yang menutup form, agar kegagalan tidak hilang diam-diam.
        if (saved === true) handleClose();
      } finally {
        submitLockRef.current = false;
        helpers.setSubmitting(false);
      }
    },
  });

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    const value = e.target.value;
    const newValues = isChecked
      ? [...formik.values.sample_send_type, value]
      : formik.values.sample_send_type.filter((item) => item !== value);
    formik.setFieldValue("sample_send_type", newValues);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose?.();
  };

  // Selama request berjalan form tetap terbuka; info ini yang boleh ditutup user.
  const requestClose = () => {
    if (isSaving) {
      setShowSendingInfo(true);
      return;
    }
    handleClose();
  };

  useEffect(() => {
    // Jangan biarkan dialog info terbuka lagi pada percobaan simpan berikutnya.
    if (!isSaving) setShowSendingInfo(false);
  }, [isSaving]);

  // Tutup dengan tombol ESC. Listener di fase capture supaya sempat melihat
  // dialog konfirmasi yang masih terbuka: Escape diarahkan ke dialog itu, bukan
  // ke modal pengiriman.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (document.getElementById("react-confirm-alert")) return;
      if (showSendingInfo) {
        setShowSendingInfo(false);
        return;
      }
      requestClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen, isSaving, showSendingInfo]);

  if (!isOpen) return null;

  const showError = (name) =>
    (formik.touched[name] || formik.submitCount > 0 || formik.dirty) &&
    formik.errors[name];

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.5)",
          zIndex: 1040,
        }}
        onClick={requestClose}
      />
      {/* Modal */}
      <div
        className="modal fade show"
        id="formSendLab"
        aria-labelledby="formSendLabModal"
        aria-modal="true"
        role="dialog"
        style={{ display: "block", zIndex: 1050, overflow: "visible" }}
      >
        <div
          className="modal-dialog"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content" style={{ overflow: "visible" }}>
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title" id="formSendLabModal">
                Kirim Sampel ke Lab
              </h5>
              <button
                type="button"
                className="close"
                onClick={requestClose}
                aria-label="Tutup"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            {/* Body */}
            <div
              className="modal-body"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                overflowY: "auto",
                maxHeight: "calc(100vh - 250px)",
              }}
            >
              <div className="form-col">
                <InputSelect
                  label="Kirim Sampel ke Lab rujukan Pemeriksaan PCR (Tingkat 3 / Tingkat 4/ Tingkat 5)"
                  data={labOptions}
                  value={
                    labOptions.find(
                      (i) => i.value === formik.values.destination_lab_id
                    ) || null
                  }
                  onChange={(opt) =>
                    formik.setFieldValue(
                      "destination_lab_id",
                      opt ? opt.value : ""
                    )
                  }
                  placeholder="Pilih Lab Tujuan"
                />
                {showError("destination_lab_id") && (
                  <p className="error-text">
                    {formik.errors.destination_lab_id}
                  </p>
                )}
              </div>

              <div className="form-col">
                <InputCheckbox
                  label="Jenis Sample yang Dikirim"
                  name="sample_send_type"
                  options={SampleSendTypeOptions}
                  onChange={(e) => {
                    handleCheckboxChange(e);
                  }}
                  selectedOption={formik.values.sample_send_type}
                  onBlur={formik.handleBlur}
                  isRequired={true}
                />
                {formik.errors.sample_send_type && (
                  <p className="error-text">{formik.errors.sample_send_type}</p>
                )}
              </div>

              {/* sample_send_date */}
              <div className="form-col">
                <InputDate
                  label="Tanggal pengiriman sampel"
                  name="sample_send_date"
                  value={formik.values.sample_send_date}
                  onChange={(e) => {
                    const formatted = e ? moment(e).format("YYYY-MM-DD") : "";
                    formik.setFieldValue("sample_send_date", formatted);
                  }}
                  placeholder="Pilih Tanggal Kirim"
                  dateFormat="YYYY-MM-DD"
                  isRequired={formik.values.sample_send_type?.length > 0}
                />
                {formik.errors.sample_send_date && (
                  <p className="error-text">{formik.errors.sample_send_date}</p>
                )}
              </div>
            </div>
            <div className="alert alert-info mb-0" role="alert">
              Pastikan semua data sudah benar sebelum disimpan.
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <Button variant="tertiary" size="normal" onClick={requestClose}>
                Batal
              </Button>
              <Button
                variant="primary"
                size="normal"
                onClick={formik.handleSubmit}
                isDisabled={!formik.isValid || formik.isSubmitting || isSaving}
              >
                {formik.isSubmitting ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isSaving && showSendingInfo && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setShowSendingInfo(false)}
        >
          <div
            className="modal-content"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="sending-info-title"
            style={{ maxWidth: 360, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5 className="modal-title" id="sending-info-title">
                Data sedang dikirim
              </h5>
            </div>
            <div className="modal-body">
              Tunggu sampai proses selesai. Form akan tertutup sendiri setelah
              data tersimpan.
            </div>
            <div className="modal-footer">
              <Button
                variant="tertiary"
                size="normal"
                onClick={() => setShowSendingInfo(false)}
                autoFocus
              >
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalSendLab;
